import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box, Grid, Card, CardContent, CardActionArea, CardActions, Typography,
  Chip, CircularProgress, Button, IconButton, Tooltip, Select, MenuItem,
  FormControl, InputLabel, LinearProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WorkIcon from '@mui/icons-material/Work';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { getApplications, getStats, updateApplication, deleteApplication, syncGmail, getGmailStatus, getGmailAuthUrl } from '../services/api';
import { useNotification } from '../context/NotificationContext';
import TailorPromptModal from '../components/TailorPromptModal';
import EmptyState from '../components/EmptyState';

// Gmail sync is currently dev-only: in production the OAuth client isn't
// configured for Railway's HTTPS domain and credentials.json isn't present
// on the server. Hide the Sync Gmail button in envs where the feature flag
// isn't explicitly opted in. Set REACT_APP_FEATURE_GMAIL=true locally to
// use Gmail sync in development.
const GMAIL_ENABLED = process.env.REACT_APP_FEATURE_GMAIL === 'true';

const STATUS_COLORS = {
  saved: 'default',
  applied: 'primary',
  interview_scheduled: 'warning',
  offer: 'success',
  rejected: 'error',
};

// i18n keys for status — labels resolve at render time via t().
const STATUS_KEY = {
  saved: 'dashboard.status_saved',
  applied: 'dashboard.status_applied',
  interview_scheduled: 'dashboard.status_interview',
  offer: 'dashboard.status_offer',
  rejected: 'dashboard.status_rejected',
};

const STATUS_ICONS = {
  saved: <BookmarkIcon fontSize="small" />,
  applied: <WorkIcon fontSize="small" />,
  interview_scheduled: <ScheduleIcon fontSize="small" />,
  offer: <CheckCircleIcon fontSize="small" />,
  rejected: <CancelIcon fontSize="small" />,
};

/**
 * PipelineCard — single stage card. Mirrors the Home page pipeline visual
 * so both surfaces feel like the same product. Clicking filters the
 * application list to that stage.
 */
function PipelineCard({ label, count, stageKey, isActive, onClick }) {
  const isEmpty = count === 0;
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        bgcolor: isEmpty ? 'transparent' : 'background.paper',
        borderColor: isActive
          ? 'primary.main'
          : (isEmpty ? 'divider' : 'primary.light'),
        borderWidth: isActive ? 2 : 1,
        opacity: isEmpty ? 0.6 : 1,
        transition: 'all 0.15s',
      }}
    >
      <CardActionArea
        onClick={() => onClick(stageKey)}
        sx={{ px: { xs: 1, sm: 1.5 }, py: 1.25, height: '100%' }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: { xs: 10, sm: 11 }, lineHeight: 1.2, display: 'block' }}
        >
          {label}
        </Typography>
        <Typography
          variant="h5"
          fontWeight={600}
          mt={0.25}
          sx={{ fontSize: { xs: 20, sm: 22 } }}
        >
          {count}
        </Typography>
      </CardActionArea>
    </Card>
  );
}

function ApplicationCard({ app, onStatusUpdate, onApplyClick, onDeleteClick }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(app.status);

  const matchingSkills = (() => {
    try { return JSON.parse(app.matching_skills || '[]'); } catch { return []; }
  })();
  const missingSkills = (() => {
    try { return JSON.parse(app.missing_skills || '[]'); } catch { return []; }
  })();

  const score = Math.round(app.match_score || 0);
  const totalSkills = matchingSkills.length + missingSkills.length;
  const hasScore = app.match_score > 0;

  const handleStatusChange = async (newStatus) => {
    setStatus(newStatus);
    setEditing(false);
    await updateApplication(app.id, { status: newStatus });
    onStatusUpdate();
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', '&:hover': { borderColor: 'text.secondary' }, transition: 'border-color 0.15s', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        {/* Header — role/company on the left, score + edit icon on the right.
            Mirrors the Jobs card pattern (score number + icon) for visual
            consistency across pages. */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={hasScore ? 1.5 : 1}>
          {/* minWidth:0 is the flex-truncate trick — without it, long role
              titles force the flex item to grow past the card edge and
              shove the header-right icons out of view. */}
          <Box flex={1} pr={1} sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {app.role}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom noWrap>{app.company}</Typography>
            {app.location && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">{app.location}</Typography>
            )}
          </Box>
          <Box display="flex" alignItems="flex-start" gap={0.5}>
            {hasScore && (
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ lineHeight: 1.2 }}
                color={score >= 80 ? 'success.main' : score >= 60 ? 'warning.main' : 'text.secondary'}
              >
                {score}
              </Typography>
            )}
            <Tooltip title={t('dashboard.change_status')}>
              <IconButton
                size="small"
                onClick={() => setEditing(!editing)}
                sx={{ mt: -0.25 }}
                aria-label={t('dashboard.change_status_aria')}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('dashboard.remove_from_tracker')}>
              <IconButton
                size="small"
                onClick={() => onDeleteClick(app)}
                sx={{ mt: -0.25, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                aria-label={t('dashboard.remove_aria')}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Match + coverage — same visual treatment as Jobs card. Only shown
            when there's a score (legacy Gmail-synced apps won't have one). */}
        {hasScore && (
          <Box mb={1.5}>
            <LinearProgress
              variant="determinate"
              value={score}
              color={score >= 80 ? 'success' : score >= 60 ? 'warning' : 'inherit'}
              sx={{ borderRadius: 2, mb: 0.5, height: 6 }}
            />
            <Typography variant="caption" color="text.secondary">
              <Box component="span" fontWeight={600} color="text.primary">{t('jobs.match_label', { score })}</Box>
              {totalSkills > 0 && (
                <>
                  {' · '}
                  <Box component="span" fontWeight={600} color="text.primary">
                    {t('jobs.skills_count', { matched: matchingSkills.length, total: totalSkills })}
                  </Box>
                </>
              )}
            </Typography>
          </Box>
        )}

        {/* Status */}
        {editing ? (
          <FormControl size="small" fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t('dashboard.status_field')}</InputLabel>
            <Select value={status} label={t('dashboard.status_field')} onChange={(e) => handleStatusChange(e.target.value)}>
              {Object.entries(STATUS_KEY).map(([key, i18nKey]) => (
                <MenuItem key={key} value={key}>{t(i18nKey)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            <Chip
              icon={STATUS_ICONS[status]}
              label={STATUS_KEY[status] ? t(STATUS_KEY[status]) : status}
              color={STATUS_COLORS[status] || 'default'}
              size="small"
            />
            {app.has_tailored && (
              <Chip
                label={t('jobs.tailored_chip')}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}

        {/* Match reason — clamped to 3 lines so cards in a grid stay
            visually consistent. Full reason is always available because
            the user can click into the application detail. */}
        {app.match_reason && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: 1,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.4,
            }}
          >
            {app.match_reason}
          </Typography>
        )}

        {/* Matching skills */}
        {matchingSkills.length > 0 && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>{t('jobs.matching')}</Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {matchingSkills.slice(0, 4).map(skill => (
                <Chip key={skill} label={skill} size="small" color="success" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {/* Missing skills */}
        {missingSkills.length > 0 && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>{t('jobs.missing')}</Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {missingSkills.slice(0, 3).map(skill => (
                <Chip key={skill} label={skill} size="small" variant="outlined" sx={{ color: 'text.secondary', borderColor: 'divider' }} />
              ))}
            </Box>
          </Box>
        )}

        {app.created_at && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            {t('dashboard.added_on', { date: new Date(app.created_at).toLocaleDateString() })}
          </Typography>
        )}
      </CardContent>

      {/* Apply button */}
      {app.url && (
        <CardActions sx={{ px: 2, pb: 2, pt: 0, mt: 'auto' }}>
          <Button
            variant={status === 'saved' ? 'contained' : 'outlined'}
            size="small"
            fullWidth
            onClick={() => onApplyClick(app)}
            endIcon={<OpenInNewIcon />}
            color="primary"
          >
            {status === 'saved' ? t('dashboard.apply_now') : t('dashboard.view_job')}
          </Button>
        </CardActions>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [tailorTarget, setTailorTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // application being confirmed for deletion
  const [deleting, setDeleting] = useState(false);
  const { success, error: notifyError, info } = useNotification();

  const checkGmailStatus = async () => {
    try {
      const res = await getGmailStatus();
      setGmailConnected(res.data.connected);
    } catch (e) {
      setGmailConnected(false);
    }
  };

  const handleConnectGmail = async () => {
    try {
      const res = await getGmailAuthUrl();
      const { auth_url } = res.data;
      window.open(auth_url, '_blank', 'width=500,height=600');
      info(t('dashboard.gmail_auth_help'));
      // Poll for connection after a delay
      setTimeout(checkGmailStatus, 5000);
    } catch (e) {
      notifyError(e.friendlyMessage || t('dashboard.gmail_auth_failed'));
    }
  };

  const handleGmailSync = async () => {
    setSyncing(true);
    try {
      const res = await syncGmail();
      const { matched, updated, unmatched, message } = res.data;
      if (updated > 0) {
        success(message || t('dashboard.gmail_updated', { updated, matched }));
      } else if (matched > 0) {
        info(message || t('dashboard.gmail_no_changes', { matched }));
      } else {
        info(
          unmatched
            ? t('dashboard.gmail_no_matches', { unmatched })
            : t('dashboard.gmail_no_emails')
        );
      }
      await loadData();
    } catch (e) {
      notifyError(e.friendlyMessage || t('dashboard.gmail_sync_failed'));
    }
    setSyncing(false);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [appsRes, statsRes] = await Promise.all([getApplications(), getStats()]);
      setApps(appsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  // Confirmed-delete flow. We show a confirmation dialog first so a mis-tap
  // doesn't nuke a tracked application. Tailored resume (if any) goes with
  // it — the DB has ON DELETE cascade semantics effectively via the FK.
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteApplication(deleteTarget.id);
      success(t('dashboard.delete_success', { role: deleteTarget.role }));
      setDeleteTarget(null);
      await loadData();
    } catch (e) {
      notifyError(e.friendlyMessage || t('dashboard.delete_failed'));
    }
    setDeleting(false);
  };

  useEffect(() => {
    loadData();
    // Skip the Gmail status probe when the feature is off — avoids a needless
    // 400 in prod where credentials.json is not deployed.
    if (GMAIL_ENABLED) checkGmailStatus();
  }, []);

  // Hide legacy Gmail-synced apps that have no job description — they can't be
  // tailored, and the "Apply" flow would just surface the "can't tailor" modal.
  const tailorable = apps.filter(a => a.description && a.description.trim());
  const filtered = filter === 'all' ? tailorable : tailorable.filter(a => a.status === filter);

  if (loading) return <Box display="flex" justifyContent="center" mt={10}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      {/* Pipeline strip — mirrors the Home page so both surfaces feel like
          the same product. Clicking a stage filters the list below. The
          response-rate + total appear as a small caption underneath since
          they're metadata, not stages of the funnel. */}
      {stats && (
        <Box mb={3}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 1,
            }}
          >
            <PipelineCard
              label={t('home.pipeline_saved')}
              count={stats.by_status?.saved || 0}
              stageKey="saved"
              isActive={filter === 'saved'}
              onClick={setFilter}
            />
            <PipelineCard
              label={t('home.pipeline_applied')}
              count={stats.by_status?.applied || 0}
              stageKey="applied"
              isActive={filter === 'applied'}
              onClick={setFilter}
            />
            <PipelineCard
              label={t('home.pipeline_interview_scheduled')}
              count={stats.by_status?.interview_scheduled || 0}
              stageKey="interview_scheduled"
              isActive={filter === 'interview_scheduled'}
              onClick={setFilter}
            />
            <PipelineCard
              label={t('home.pipeline_offer')}
              count={stats.by_status?.offer || 0}
              stageKey="offer"
              isActive={filter === 'offer'}
              onClick={setFilter}
            />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1, fontSize: 12 }}
          >
            {t('dashboard.stats_summary', {
              total: stats.total,
              rate: stats.response_rate,
            })}
          </Typography>
        </Box>
      )}

      {/* Page title moved to the top app bar — see Navbar.TITLE_BY_PATH.
          This row now only hosts the Gmail action (when the feature flag
          is on), right-aligned above the filter chips. */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} minHeight={32}>
        {GMAIL_ENABLED && (
          gmailConnected ? (
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={16} /> : null}
              onClick={handleGmailSync}
              disabled={syncing}
            >
              {syncing ? t('dashboard.syncing') : t('dashboard.sync_gmail')}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleConnectGmail}
              color="primary"
            >
              {t('dashboard.sync_gmail')}
            </Button>
          )
        )}
      </Box>

      {/* Filter */}
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        {['all', 'saved', 'applied', 'interview_scheduled', 'offer', 'rejected'].map(s => (
          <Chip
            key={s}
            label={s === 'all' ? t('dashboard.filter_all') : t(STATUS_KEY[s])}
            onClick={() => setFilter(s)}
            color={filter === s ? 'primary' : 'default'}
            variant={filter === s ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      {/* Applications Grid */}
      {filtered.length === 0 ? (
        filter === 'all' ? (
          <EmptyState
            icon={<BookmarkIcon />}
            title={t('dashboard.empty_all_title')}
            body={t('dashboard.empty_all_body')}
            action={{
              label: t('dashboard.empty_all_cta'),
              onClick: () => navigate('/jobs'),
              icon: <SearchIcon />,
            }}
            sx={{ mt: 4 }}
          />
        ) : (
          <EmptyState
            icon={<BookmarkIcon />}
            title={t('dashboard.empty_filtered_title', { status: t(STATUS_KEY[filter]) })}
            body={t('dashboard.empty_filtered_body')}
            secondary={{
              label: t('dashboard.empty_filtered_clear'),
              onClick: () => setFilter('all'),
            }}
            sx={{ mt: 4 }}
          />
        )
      ) : (
        <Grid container spacing={2}>
          {filtered.map(app => (
            <Grid item xs={12} sm={6} md={4} key={app.id}>
              <ApplicationCard
                app={app}
                onStatusUpdate={loadData}
                onApplyClick={(appData) => setTailorTarget(appData)}
                onDeleteClick={(appData) => setDeleteTarget(appData)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <TailorPromptModal
        open={!!tailorTarget}
        onClose={() => setTailorTarget(null)}
        application={tailorTarget}
      />

      {/* Delete confirmation — prevents accidental taps on the trash icon */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>{t('dashboard.delete_dialog_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            <Trans
              i18nKey="dashboard.delete_dialog_body"
              values={{
                role: deleteTarget?.role || '',
                company: deleteTarget?.company || '',
              }}
              components={{ strong: <strong /> }}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            color="inherit"
            size="small"
            disabled={deleting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            size="small"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
          >
            {deleting ? t('common.remove_progress') : t('common.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
